import { FormEventHandler, useEffect, useState } from "react"
import { Link, useForm } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Field } from "../../components/Field"
import { TextInput } from "../../components/TextInput"
import { isIsraeliMobilePhone, sanitizePhoneInput } from "../../utils/phone"

type Form = {
  identifier: string
  password: string
}

type Props = {
  otp_sent?: boolean
  otp_phone?: string
}

export default function Login({ otp_sent = false, otp_phone = "" }: Props) {
  const [mode, setMode] = useState<"password" | "otp">(otp_sent ? "otp" : "password")
  const [otpStep, setOtpStep] = useState(otp_sent)
  const [otpPhone, setOtpPhone] = useState(otp_phone)
  const [otpPhoneError, setOtpPhoneError] = useState("")
  const [resendCountdown, setResendCountdown] = useState(otp_sent ? 60 : 0)
  const { data, setData, post, processing, errors } = useForm<Form>({
    identifier: "",
    password: "",
  })
  const otpRequest = useForm({ phone: otpPhone })
  const otpVerify = useForm({ phone: otpPhone, code: "" })

  useEffect(() => {
    if (otp_sent) {
      setMode("otp")
      setOtpStep(true)
      setResendCountdown((current) => current || 60)
    }
  }, [otp_sent])

  useEffect(() => {
    if (resendCountdown <= 0) return
    const timer = window.setInterval(() => setResendCountdown((current) => Math.max(0, current - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [resendCountdown])

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    post("/login")
  }

  return (
    <AppLayout title="כניסה לחשבון">
      <div className="mx-auto grid max-w-md gap-4 pt-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold">כניסה לחשבון</h1>
          <p className="mt-2 text-slate-600">כדי לנהל את הכרטיסים שלך</p>
        </div>

        <div className="mb-2 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
          <button type="button" onClick={() => setMode("password")} className={`rounded-xl px-3 py-2 text-sm font-medium ${mode === "password" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>סיסמה</button>
          <button type="button" onClick={() => setMode("otp")} className={`rounded-xl px-3 py-2 text-sm font-medium ${mode === "otp" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>קוד ב-SMS</button>
        </div>

        {mode === "password" ? (
          <form onSubmit={submit} className="glass-card space-y-4 p-6">
            <Field label="שם משתמש או אימייל" error={errors.identifier} hint="אפשר להיכנס עם אחד מהם">
              <TextInput dir="ltr" value={data.identifier} onChange={(e) => setData("identifier", e.target.value)} />
            </Field>
            <Field label="סיסמה" error={errors.password}>
              <TextInput type="password" value={data.password} onChange={(e) => setData("password", e.target.value)} />
            </Field>
            <button disabled={processing} className="btn-primary w-full bg-brand-600">כניסה</button>
          </form>
        ) : (
          <div className="glass-card space-y-4 p-6">
            {otpStep ? (
              <form onSubmit={(event) => { event.preventDefault(); otpVerify.setData("phone", otpPhone); otpVerify.post("/login/otp/verify") }} className="space-y-4">
                <Field label="קוד האימות" error={otpVerify.errors.code || otpVerify.errors.phone} hint={`שלחנו קוד ל-${otpPhone}`}>
                  <TextInput dir="ltr" inputMode="numeric" maxLength={6} autoFocus value={otpVerify.data.code} onChange={(e) => otpVerify.setData("code", e.target.value.replace(/\D/g, ""))} />
                </Field>
                <button disabled={otpVerify.processing} className="btn-primary w-full bg-brand-600">אימות וכניסה</button>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <button type="button" className="text-brand-700" onClick={() => { setOtpStep(false); setOtpPhoneError(""); otpVerify.reset() }}>החלפת מספר</button>
                  <button type="button" disabled={resendCountdown > 0 || otpRequest.processing} className="text-slate-500 disabled:cursor-not-allowed disabled:opacity-50" onClick={() => { otpRequest.setData("phone", otpPhone); otpRequest.post("/login/otp/request", { onSuccess: () => setResendCountdown(60) }) }}>
                    {resendCountdown > 0 ? `שליחה חוזרת בעוד ${resendCountdown}` : "שליחת קוד מחדש"}
                  </button>
                </div>
                <button type="button" className="w-full text-sm text-brand-700" onClick={() => setMode("password")}>כניסה עם סיסמה</button>
              </form>
            ) : (
              <form onSubmit={(event) => {
                event.preventDefault()
                if (!isIsraeliMobilePhone(otpPhone)) {
                  setOtpPhoneError("הזינו מספר נייד ישראלי תקין")
                  return
                }
                setOtpPhoneError("")
                otpRequest.setData("phone", otpPhone)
                otpRequest.post("/login/otp/request", { onSuccess: () => { setOtpStep(true); setResendCountdown(60) } })
              }} className="space-y-4">
                <Field label="מספר טלפון" error={otpRequest.errors.phone || otpPhoneError} hint="נשלח אליך קוד חד-פעמי">
                  <TextInput dir="ltr" type="tel" inputMode="tel" maxLength={16} value={otpPhone} onChange={(e) => { setOtpPhone(sanitizePhoneInput(e.target.value)); setOtpPhoneError("") }} placeholder="050-0000000" />
                </Field>
                <button disabled={otpRequest.processing} className="btn-primary w-full bg-brand-600">שליחת קוד</button>
              </form>
            )}
          </div>
        )}

        <p className="text-center text-sm text-slate-600">
          אין לך חשבון? <Link href="/register" className="font-medium text-brand-700">הרשמה</Link>
        </p>
      </div>
    </AppLayout>
  )
}
